<template>
  <lateral-modal
    :title="$t(createMileageHeader)"
    :containerClass="isDragging && !transactionWithoutReceipt ? 'ModalBox--dim' : ''"
    :modalClass="isReceiptModalOpen ? 'ModalBox--receipt-open' : ''"
    :footerClass="'grid-mileage pl-3 pr-3'"
    @close="checkCloseModal"
  >
    <template slot="extraAction">
      <transactions-dropdown
        deleteText="transaction_delete_mileage"
        :transactionId.sync="transactionId"
        @confirmDelete="deleteTransaction"
      ></transactions-dropdown>
    </template>
    <template slot="content">
      <modal-drop-zone
        v-if="isDragging && !transactionWithoutReceipt"
        :showDropZone.sync="isDragging"
        @dragleave.prevent="onDragleave"
        @drop.prevent="ondrop"
        :fileCount="receiptCount"
        :showSpinner="fileUploadByDrop"
      />
      <template v-if="!isInitializing && !isSubmitting">
        <form
          id="create-mileage-form"
          class="grid-mileage"
          @submit.prevent="submitMileageHandler"
          @dragenter.prevent="onDragenter"
        >
          <div class="grid-mileage--first-column">
            <transaction-review-status-log
              :transaction="transaction"
              v-if="transactionId"
            />
            <warning-container
              :transaction="transaction"
              @showIgnoreWarning="showIgnoreWarning"
              @showTransactionModal="showTransactionModal"
            />

            <receipts-container
              ref="receiptsContainer"
              :transactionIsLocked="transactionIsLocked"
              :isProcessing="isProcessing"
              :transactionId="transactionId"
              :receipts="receipts"
              :transactionWithoutReceiptNotes="transactionWithoutReceiptNotes"
              :transactionWithoutReceipt="transactionWithoutReceipt"
              :required="fieldIsRequired('medias')"
              :expenseType="expenseType"
              @showReceiptModal="emitShowReceiptModal"
              @uploadedReceipt="uploadedReceipt"
              @sendToReview="submitMileageHandler"
            ></receipts-container>

            <div class="input--block input--block--margin input--block--padded">
              <label
                for="mileage-date"
                class="form--label"
                :class="fieldIsRequired('date')"
              >{{$t('expense_edit_expense_date').toUpperCase()}}</label>
              <date-picker-input
                :mode="'date'"
                :errorClass="$v.mileage.date.$error"
                :placeholder="'expense_edit_expense_date'"
                :value="mileage.date"
                :disabled="transactionIsLocked || isProcessing"
                @inputDate="setMileageDate"
                @hide="$v.mileage.date.$touch()"
              ></date-picker-input>
            </div>
            <template v-if="mileageCategories.length > 0">
              <div class="input--block input--block--margin input--block--padded">
                <label
                  for="mileage-category"
                  class="form--label"
                  :class="fieldIsRequired('mileageCategory')"
                >{{$t('expense_mileage_category').toUpperCase()}}</label>
                <div class="multiselect-filter">
                  <multiselect
                    :id="'expense-mileage-category-selector'"
                    :options="mileageCategories"
                    :customLabel="option => `${option.name} - ${this.currencyFormatter(option.rate)}`"
                    v-model="mileage.category"
                    :disabled="transactionIsLocked || isProcessing"
                    @open="$v.mileage.category.$touch()"
                    @input="calculateAmount"
                    :placeholder="$t('expense_mileage_category')"
                    track-by="id"
                    :selectLabel="''"
                    :allowEmpty="false"
                    :showLabels="false"
                    :selectedLabel="''"
                  >
                    <div
                      class="caret"
                      slot="caret"
                      slot-scope="{ toggle }"
                      @mousedown.prevent.stop="toggle"
                    >
                      <i
                        class="Ctrl Ctrl--16"
                        aria-hidden="true"
                      >
                        <svg
                          focusable="false"
                          aria-hidden="true"
                          aria-label="Edit"
                        >
                          <use xlink:href="#dropdown-icon"></use>
                        </svg>
                      </i>
                    </div>
                    <template slot="noOptions">
                      {{$t('expense_create_mileage_categories_no_options')}}
                    </template>
                    <template slot="noResult">
                      <span class="no-result">
                        {{$t('expense_create_mileage_categories_no_results')}}
                      </span>
                    </template>

                  </multiselect>

                </div>
              </div>
            </template>
            <template v-else>
              <div class="my-5 text-center">
                <i18n
                  path="expenses_create_mileage_no_categories_found_admin"
                  tag="span"
                  class="Fuell-Text"
                  v-if="isAdmin || isBookKeeper"
                >
                  <template v-slot:link>
                    <router-link
                      class="text-primary"
                      :to="{name:'expense_fields'}"
                    >{{$t('ACTION_CLICK_HERE')}}</router-link>
                  </template>

                </i18n>
                <span
                  v-else
                  class="Fuell-Text"
                  v-t.preserve="{path:'expense_create_mileage_no_categories_found'}"
                ></span>
              </div>
            </template>
            <div class="input--block input--block--margin input--block--padded">
              <label
                for="mileage-origin"
                class="form--label"
                :class="fieldIsRequired('origin')"
              >{{$t('expense_mileage_origin').toUpperCase()}}</label>
              <div
                class="Form-field"
                :class="{'Form--field-error' : $v.mileage.origin.name.$error}"
              >
                <google-places-autocomplete
                  :id="'mileage-origin'"
                  :value="mileage.origin.name"
                  :classname="'Input-text'"
                  :disabled="transactionIsLocked || isProcessing"
                  @placechanged="setLocation('origin', $event)"
                  @change="setLocation('origin',$event)"
                  @blur="$v.mileage.origin.name.$touch()"
                >
                </google-places-autocomplete>
              </div>
            </div>
            <div class="input--block input--block--margin input--block--padded">
              <label
                for="mileage-destination"
                class="form--label"
                :class="fieldIsRequired('destination')"
              >{{$t('expense_mileage_destination').toUpperCase()}}</label>
              <div
                class="Form-field"
                :class="{'Form--field-error' : $v.mileage.destination.name.$error}"
              >
                <google-places-autocomplete
                  :id="'mileage-destination'"
                  :value="mileage.destination.name"
                  :classname="'Input-text'"
                  :disabled="transactionIsLocked || isProcessing"
                  @blur="$v.mileage.destination.name.$touch()"
                  @placechanged="setLocation('destination',$event)"
                  @change="setLocation('destination', $event)"
                >
                </google-places-autocomplete>

              </div>
            </div>
            <div class="input--block input--block--margin input--block--padded">
              <label
                for="mileage-distance"
                class="form--label"
                :class="fieldIsRequired('distance')"
              >{{$t('expense_mileage_distance_kms').toUpperCase()}}</label>
              <div
                class="Form-field"
                :class="{'Form--field-error' : $v.distance.$error}"
              >
                <currency-input
                  class="Input-text"
                  :value="distance"
                  :placeholder="$t('expense_mileage_distance_kms')"
                  :disabled="disabledDistance"
                  @blur="$v.distance.$touch()"
                  @change="setDistance"
                  :currency="{suffix: ' Kms'}"
                  :allow-negative="false"

                >
                </currency-input>
              </div>
            </div>
            <div
              v-if="manualDistance && mileage.distance"
              class="manual-distance"
            >
              <p>
                {{$t('expense_mileage_distance_calculated', {kms: mileage.distance})}}
              </p>
            </div>
            <div
              class="map--smaller"
              v-if="hasDefinedOriginAndDestination && !googleRoutesError"
            >
              <google-maps
                :id="`google-maps-small-${mapsId}`"
                :mapClass="'map-content--small'"
                :waypoints="[this.origin, this.destination]"
                v-if="hasDefinedOriginAndDestination && !googleRoutesError"
              />
            </div>
            <div class="input--block input--block--margin input--block--padded">
              <label
                for="mileage-amount"
                class="form--label"
                :class="fieldIsRequired('amount')"
              >{{$t('expense_amount').toUpperCase()}}</label>
              <div class="Form-field">
                <currency-input
                  disabled
                  :class="'Input-text'"
                  v-model="mileage.amount"
                  :precission="3"
                  :allow-negative="false"
                  :placeholder="$t('expense_amount')"
                ></currency-input>

              </div>
            </div>
            <div class="input--block input--block--padded">
              <div class="checkbox-container">
                <input-checkbox
                  :id="'input-checkbox-mileage-round-trip-check'"
                  :value.sync="mileage.roundTrip"
                  :disabled="transactionIsLocked || isProcessing"
                  @click="toggleRoundTrip($event)"
                >
                  <template v-slot:input-label>
                    <span
                      class="ml-2"
                      :class="fieldIsRequired('roundTrip')"
                    >{{$t('expense_create_mileage_round_trip_check')}}</span>
                  </template>
                </input-checkbox>

              </div>
            </div>

            <div class="input--block input--block--margin input--block--padded">
              <label
                for="transaction-category-input"
                class="form--label"
                :class="fieldIsRequired('paymentMethod')"
              >
                {{
                    $t('expense_payment_method').toUpperCase()
                  }}
              </label>
              <div
                class="multiselect-filter"
                :class="{'multiselect--error' : $v.paymentMethod.$error}"
              >
                <multiselect
                  v-if="paymentMethods.length > 0"
                  :id="'expense-payment-method-selector'"
                  :options="paymentMethods"
                  :customLabel="option => paymentMethodsLabel(option)"
                  v-model="paymentMethod"
                  :disabled="transactionIsLocked || isProcessing"
                  :placeholder="$t('expense_payment_method')"
                  track-by="id"
                  :allowEmpty="false"
                  :showLabels="false"
                  @open="$v.paymentMethod.$touch()"
                >
                  <div
                    class="caret"
                    slot="caret"
                    slot-scope="{ toggle }"
                    @mousedown.prevent.stop="toggle"
                  >
                    <i
                      class="Ctrl Ctrl--16"
                      aria-hidden="true"
                    >
                      <svg
                        focusable="false"
                        aria-hidden="true"
                        e
                        aria-label="Edit"
                      >
                        <use xlink:href="#dropdown-icon"></use>
                      </svg>
                    </i>
                  </div>
                  <template
                    slot="singleLabel"
                    slot-scope="props"
                  >
                    <template v-if="props.option &&props.option.officePaymentMethod">
                      <span>{{props.option.officePaymentMethod.name}}</span> <span class="multiselect--extra-info-gray"> - {{props.option.officePaymentMethod.repayable ? $t('expenses_expense_is_reimbursable') : $t('expenses_expense_is_not_reimbursable')}}</span>
                    </template>
                  </template>

                </multiselect>
              </div>
            </div>
            <!-- budget here-->
            <div class="input--block input--block--margin input--block--padded">
              <label
                for="mileage-amount"
                class="form--label"
                :class="fieldIsRequired('budget')"
              >{{$t('BUDGETS').toUpperCase()}}</label>
              <div class="multiselect-filter">
                <multi-select-input
                  id="transaction-budget-selector"
                  :options="generateBudgetList()"
                  :placeholder="$t('BUDGETS')"
                  emitFullValue
                  :value="budget"
                  :disabled="transactionIsLocked || isProcessing"
                  :customLabel="'name'"
                  searchable
                  @inputSelected="setBudget($event, INCLUDE_IN_TRANSACTION_TYPES.MILEAGE)"
                />
              </div>
            </div>

            <div class="input--block input--block--margin input--block--padded">
              <label
                for="mileage-notes"
                class="form--label"
                :class="fieldIsRequired('notes')"
              >{{$t('expense_notes').toUpperCase()}}</label>
              <div class="Form-field">
                <input
                  type="text"
                  class="Input-text"
                  v-model.trim="notes"
                  :disabled="transactionIsLocked || isProcessing"
                  name="expense-notes"
                  :placeholder="$t('expense_notes')"
                >
              </div>
            </div>

            <template v-if="isFetchingBudgetTagTypes">
              <div class="d-flex justify-content-center w-100">
                <spinner />
              </div>
            </template>

            <template v-if="!isFetchingBudgetTagTypes && selectedBudgetMandatoryTagTypes.length > 0">
              <div
                class="input--block input--block--margin input--block--padded"
                v-for="item in selectedBudgetMandatoryTagTypes"
                :key="item.id"
              >
                <label
                  for="transaction-tag"
                  class="form--label"
                  :class="{ 'u-required': item.mandatory }"
                >{{ item.tagType.name.toUpperCase() }}</label>
                <div class="multiselect-custom-field">
                  <option-settings-input
                    id="transaction-tag"
                    :disabled="transactionIsLocked || isProcessing"
                    :allowMultiple="item.tagType.multipleOptionsEnabled"
                    :allowTaggable="item.tagType.customOptionEnabled"
                    :alternateOptions="item.tagType.tags"
                    :overrideValue="handleCustomFieldValues(item.tagType)"
                    :noOptionsLabel="
                      'company_create_transaction_field_options_no_options'
                    "
                    :noResultLabel="'multiselect_no_result_slot_default'"
                    :placeholder="item.tagType.name"
                    :tag-placeholder="
                      $t(
                        handleCreateTagPlaceholder(
                          item.tagType.customOptionEnabled
                        )
                      )
                    "
                    internalSearch
                    emitTagTypeOnClose
                    :searchable="true"
                    :customField="item.tagType"
                    :class="{ 'multiselect--error': item.invalid }"
                    closeOnSelect
                    useOptionSlot
                    @select="addTagToTransactionTag(item, $event)"
                    @remove="removeTagFromTransactionTag"
                    @tag="handleCreateOption(item, $event)"
                  ></option-settings-input>
                </div>
              </div>
            </template>
            <template v-if="!isFetchingBudgetTagTypes && selectedBudgetNoMandatoryTagTypes.length > 0">
              <div
                class="input--block input--block--margin input--block--padded"
                v-for="item in selectedBudgetNoMandatoryTagTypes"
                :key="item.id"
              >
                <label
                  for="transaction-tag"
                  class="form--label"
                  :class="{ 'u-required': item.mandatory }"
                >{{ item.tagType.name.toUpperCase() }}</label>
                <div class="multiselect-custom-field">
                  <option-settings-input
                    id="transaction-tag"
                    :disabled="transactionIsLocked || isProcessing"
                    :allowMultiple="item.tagType.multipleOptionsEnabled"
                    :allowTaggable="item.tagType.customOptionEnabled"
                    :alternateOptions="item.tagType.tags"
                    :overrideValue="handleCustomFieldValues(item.tagType)"
                    :noOptionsLabel="
                      'company_create_transaction_field_options_no_options'
                    "
                    :noResultLabel="'multiselect_no_result_slot_default'"
                    :placeholder="item.tagType.name"
                    :tag-placeholder="
                      $t(
                        handleCreateTagPlaceholder(
                          item.tagType.customOptionEnabled
                        )
                      )
                    "
                    internalSearch
                    emitTagTypeOnClose
                    :searchable="true"
                    :customField="item.tagType"
                    :class="{ 'multiselect--error': item.invalid }"
                    closeOnSelect
                    useOptionSlot
                    @select="addTagToTransactionTag(item, $event)"
                    @remove="removeTagFromTransactionTag"
                    @tag="handleCreateOption(item, $event)"
                  ></option-settings-input>
                </div>
              </div>
            </template>

          </div>
          <div
            class="grid-mileage--map-column"
            :class="mapColumnClass"
          >
            <google-maps
              :id="`google-maps-${mapsId}`"
              :mapClass="'map-content'"
              :waypoints="[this.origin, this.destination]"
              v-if="hasDefinedOriginAndDestination && !googleRoutesError"
            ></google-maps>
            <div
              class="d-flex justify-content-center align-items-center map-content"
              v-else
            >
              <template v-if="!googleRoutesError">
                <span class="Fuell-Text">{{$t('expense_create_mileage_map_default')}}</span>
              </template>
              <template v-else-if="googleRoutesError && manualDistance">
                <span class="text-danger">{{$t('expense_create_mileage_map_route_error_manual')}}</span>
              </template>
              <template v-else>
                <span class="text-danger">{{$t('expense_create_mileage_map_route_error')}}</span>
              </template>

            </div>
          </div>
        </form>

      </template>
      <template v-else>
        <div class="w-100 h-100 d-flex justify-content-center align-items-center">
          <spinner></spinner>
        </div>
      </template>
    </template>
    <template
      slot="footer"
      v-if="!isInitializing"
    >
      <div
        class="grid-mileage--first-column py-2 px-2"
        v-if="showSaveAsDraftButton || showSendToReviewButton || showSaveButton"
      >
        <button
          class="Fuell-Button orange mb-2"
          v-if="showSaveAsDraftButton"
          @click="draftHandler"
          :disabled="transactionIsLocked || isSubmitting || !isDirty || isProcessing"
          :class="{'disabled' : transactionIsLocked || isSubmitting || !isDirty || isProcessing}"
        >
          <span class="Fuell-Button--label w-100 text-center">
            {{$t('expenses_save_as_draft')}}
          </span>

        </button>
        <button
          class="Fuell-Button blue mb-2"
          v-if="showSendToReviewButton"
          @click="submitMileageHandler"
          :disabled="transactionIsLocked || isSubmitting || !canBeSubmitted  || isProcessing"
          :class="{'disabled' : transactionIsLocked || isSubmitting || !canBeSubmitted  || isProcessing}"
        >
          <span class="Fuell-Button--label w-100 text-center">
            {{$t('expenses_send_to_review')}}
          </span>
        </button>
        <button
          class="Fuell-Button blue mb-2"
          v-if="showSaveButton"
          @click="submitMileageHandler"
          :disabled="transactionIsLocked || isSubmitting || !isDirty  || isProcessing"
          :class="{'disabled' : transactionIsLocked || isSubmitting || !isDirty  || isProcessing}"
        >
          <span class="Fuell-Button--label w-100 text-center">
            {{$t('transactions_edit_transaction_save')}}
          </span>
        </button>
      </div>
      <template v-if="!transactionIsLocked && reviewStatus !== 'DRAFT' && transactionId && !isInExpensesSection && !isDirty && !isProcessing">
        <funnel-actions
          class="grid-mileage--first-column py-3"
          @reviewStatusChanged="emitReviewStatusChanged"
          :reviewStatus.sync="reviewStatus"
          :budgetOwner="isBudgetOwner"
          :expenseOwner="transactionOwner"
          :transactionId.sync="transactionId"
        />
      </template>

    </template>
    <template slot="modals">
      <transaction-not-receipt-modal
        v-if="showNotReceiptModalFlag"
        :open.sync="showNotReceiptModalFlag"
        @emitNotReceiptReason="handleNotReceipt"
      ></transaction-not-receipt-modal>
      <confirm-modal
        v-if="showSendToReviewWithoutReceipt"
        :open.sync="showSendToReviewWithoutReceipt"
        title="expenses_send_to_review"
        message="expenses_send_to_review_description"
        modalClass="ModalBox--400"
        acceptText="expenses_upload_receipt"
        dismissText="transaction_dont_have_receipt"
        acceptBtnClass="blue"
        @accept="showReceiptsContainer"
        @dismiss="showNotReceiptModal"
      ></confirm-modal>
      <confirm-modal
        v-if="showCloseWarning"
        :open.sync="showCloseWarning"
        title="transaction_close_without_save"
        message="transaction_close_without_save_description"
        modalClass="ModalBox--400"
        acceptText="transaction_close_delete"
        dismissText="transaction_close_delete_draft"
        acceptBtnClass="Button--danger"
        @accept="closeModal"
        @dismiss="draftHandler"
      ></confirm-modal>
      <confirm-modal
        v-if="showIgnoreWarningModal"
        :open.sync="showIgnoreWarningModal"
        modalClass="ModalBox--400"
        :title="'transaction_warning_ignore_modal'"
        :message="'transaction_warning_ignore_message'"
        :acceptText="'transaction_warning_ignore'"
        :dismissText="'ACTION_CANCEL'"
        acceptBtnClass="blue"
        dismissBtnClass="red"
        @accept="ignoreWarning"
        @dismiss="dismissWarning"
      ></confirm-modal>
      <confirm-modal
        v-if="showUploadInProgress"
        :open.sync="showUploadInProgress"
        :modalClass="'ModalBox--400'"
        :title="''"
        :message="''"
        :acceptText="'transaction_upload_in_progress_understood'"
        acceptBtnClass="blue"
        closeButton
        noPadding
        noMargin
        hideDismissButton
        @accept="uploadingAccept"
      >
        <template v-slot:content>
          <div class="transaction-upload-in-progress">
            <img
              :src="require('@/assets/img/upload-in-progress.svg')"
              alt=""
            />
            <p class="transaction-upload-in-progress--title">{{ $t('transaction_upload_in_progress_title') }}</p>
            <p class="transaction-upload-in-progress--description">{{ $t('transaction_upload_in_progress_description') }}</p>
          </div>
        </template>
      </confirm-modal>
      <choose-media-type-modal
        v-if="showMediaTypeModalFlag"
        :open.sync="showMediaTypeModalFlag"
        @typeSelected="typeIsSelected"
      ></choose-media-type-modal>
    </template>
  </lateral-modal>
</template>

<script>
import { REVIEW_STATUS } from '@/constants/reviewStatus'
import { INCLUDE_IN_TRANSACTION_TYPES } from '../../_constants/constants'

import _modalHandlerMixin from '@/components/Commons/_mixins/_modalHandlerMixin'
import _transactionAndExpenseCommon from '../../_mixins/_transactionAndExpenseCommon'
import _transactionReceiptHandler from '../../_mixins/_transactionReceiptDragAndDropHandler'
import _expenseBudgetSelectionHandlerMixin from '../../_mixins/_expenseBudgetSelectionHandlerMixin'
import _revissionFunnelActionsMixin from '../../_mixins/funnel/_revissionFunnelActionsMixin'
import _transactionsStatusHandlerMixin from '../../_mixins/_transactionStatusHandlerMixin'
import _transactionsCustomFieldsHandler from '../../_mixins/_transactionsCustomFieldsHandler'
import _transactionsValidationHandlerMixin from '../../_mixins/_transactionsValidationHandlerMixin'
import _transactionsTicketHandlerMixin from '../../_mixins/_transactionsTicketHandlerMixin'

import InputCheckbox from '@/components/Commons/Forms/InputCheckbox'
import MultiSelectInput from '@/components/Commons/Forms/MultiSelectInput'
import Multiselect from 'vue-multiselect'
import { CurrencyInput } from 'vue-currency-input'
import DatePickerInput from '@/components/Commons/Forms/DatePickerInput'
import Spinner from '@/components/Commons/Spinner'
import ModalDropZone from '@/components/Commons/ModalDropZone'
import FunnelActions from '../funnel/FunnelActions.vue'
import TransactionReviewStatusLog from '../funnel/statusLog/TransactionReviewStatusLog.vue'
import ReceiptsContainer from '@/components/Commons/ReceiptsContainer.vue'
import OptionSettingsInput from '@/modules/company/_components/_shared/OptionSettingsInput'
import WarningContainer from '../_shared/WarningContainer.vue'
import GooglePlacesAutocomplete from '@/components/Google/GooglePlacesAutocomplete'
import GoogleMaps from '@/components/Google/GoogleMaps'
import LateralModal from '@/components/Commons/Modals/LateralModal'

const TransactionsDropdown = () => import('../_shared/TransactionsDropdown')
const ConfirmModal = () => import('@/components/Commons/ConfirmModal')
const ChooseMediaTypeModal = () => import('@/modules/transactions/_components/media/ChooseMediaTypeModal.vue')
const TransactionNotReceiptModal = () => import('../_shared/TransactionNotReceiptModal')
const generateMileageBaseModel = () => {
  return {
    date: null,
    origin: {
      name: '',
      position: 'ORIGIN',
      lat: null,
      lng: null
    },
    category: '',
    destination: {
      name: '',
      position: 'DESTINATION',
      lat: null,
      lng: null
    },
    distance: 0,
    roundTrip: false,
    notes: '',
    amount: null,
    budget: {},
    paymentMethod: ''
  }
}

export default {
  name: 'CreateMileageModal',
  mixins: [_modalHandlerMixin, _transactionAndExpenseCommon, _transactionReceiptHandler, _expenseBudgetSelectionHandlerMixin, _revissionFunnelActionsMixin, _transactionsStatusHandlerMixin, _transactionsCustomFieldsHandler, _transactionsValidationHandlerMixin, _transactionsTicketHandlerMixin],
  components: {
    WarningContainer,
    InputCheckbox,
    MultiSelectInput,
    Multiselect,
    GooglePlacesAutocomplete,
    GoogleMaps,
    ReceiptsContainer,
    CurrencyInput,
    DatePickerInput,
    Spinner,
    ModalDropZone,
    FunnelActions,
    OptionSettingsInput,
    TransactionReviewStatusLog,
    ConfirmModal,
    TransactionsDropdown,
    LateralModal,
    ChooseMediaTypeModal,
    TransactionNotReceiptModal
  },
  props: {
    transactionId: {
      type: [Number, String]
    }
  },
  data () {
    return {
      showSendToReviewWithoutReceipt: false,
      showNotReceiptModalFlag: false,
      withoutTicket: {},
      expenseType: 'MILEAGE',
      mapsId: 1,
      mileage: {},
      paymentMethod: '',
      origin: {
        position: 'ORIGIN',
        name: '',
        lat: null,
        lng: null
      },
      destination: {
        position: 'DESTINATION',
        name: '',
        lat: null,
        lng: null

      },
      budget: null,
      notes: '',
      receiptLocalId: 1,
      originalDistance: null,
      originalAmount: null,
      isInitializing: false,
      googleRoutesError: false,
      originalExpense: {},
      isSubmitting: false,
      transactionTags: [],
      manualDistance: false,
      distance: null,
      originalMileageData: {},
      originalDestinationData: {},
      originalOriginData: {},
      originalBudgetId: null,
      originalPaymentMethod: '',
      originalTransactionTags: [],
      showIgnoreWarningModal: false
    }
  },
  computed: {
    disabledDistance () {
      if (this.transactionIsLocked || this.isProcessing) {
        return true
      } else if (this.mileage.origin.lat && this.mileage.destination.lat) {
        return false
      }
      return false
    },
    INCLUDE_IN_TRANSACTION_TYPES () {
      return INCLUDE_IN_TRANSACTION_TYPES
    },
    mapColumnClass () {
      if (this.transactionId) {
        if (this.transaction?.warnings?.length > 0) {
          return 'grid-mileage-map--column--with-warnings'
        } else {
          return 'grid-mileage--map-column--with-info'
        }
      }
      return ''
    },
    mileageCategories () {
      return this.$store.getters['CompanyStore/ExpensesSettingsStore/mileageCategories'] || []
    },
    canBeSubmitted () {
      return !this.$v.$invalid && this.budgetTagTypes.filter(item => item.mandatory && !item.filled).length === 0
    },
    expenseTypes () {
      return this.$store.getters['ConfigStore/privateConfig'].expense_types
    },
    hasDefinedOriginAndDestination () {
      return this.mileage.origin.lat && this.mileage.destination.lat
    },
    createMileageHeader () {
      return this.transactionId ? 'expense_edit_mileage_header' : 'expense_create_mileage_header'
    },
    isSuperBook () {
      return this.$store.getters['LoginStore/isSuperBookKeeper']
    },
    isDirty () {
      if (JSON.stringify(this.originalMileageData) !== JSON.stringify(this.mileage)) {
        return true
      }

      if (JSON.stringify(this.originalOriginData) !== JSON.stringify(this.origin)) {
        return true
      }

      if (JSON.stringify(this.originalDestinationData) !== JSON.stringify(this.destination)) {
        return true
      }

      if (JSON.stringify(this.budget) !== JSON.stringify(this.originalBudgetId)) {
        return true
      }

      if (JSON.stringify(this.originalPaymentMethod) !== JSON.stringify(this.paymentMethod)) {
        return true
      }

      if (JSON.stringify(this.originalTransactionTags) !== JSON.stringify(this.transactionTags)) {
        return true
      }

      if (this.expensesReceipts.length > 0) {
        return true
      }
      return false
    }
  },
  methods: {

    currencyFormatter (number) {
      if (!number) return ''
      const currencyFormatter = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', maximunFractionDigits: 3, minimumFractionDigits: 3, currencyDisplay: 'symbol' })
      return currencyFormatter.format(number)
    },
    showReceiptsContainer () {
      this.$refs.receiptsContainer.addReceipt(true)
    },
    showNotReceiptModal () {
      this.showNotReceiptModalFlag = !this.showNotReceiptModalFlag
    },
    handleNotReceipt (reason) {
      this.withoutTicket.withoutTicket = true
      this.withoutTicket.withoutTicketNotes = reason
      this.submitMileageHandler()
    },
    setField (fieldName, value) {
      this[fieldName] = value
    },
    setMileageDate (event) {
      this.mileage.date = event
    },
    setDistance (event) {
      this.$set(this.mileage, 'distanceCustom', event)
      this.distance = event
      this.manualDistance = true
      this.calculateAmount()
    },
    setLocation (field, event) {
      this.mileage[field].name = event.formatted_address ? event.formatted_address : event
      this.mileage[field].lat = event.latitude ? event.latitude : null
      this.mileage[field].lng = event.longitude ? event.longitude : null
      this[field].name = event.formatted_address ? event.formatted_address : event
      this[field].lat = event.latitude ? event.latitude : null
      this[field].lng = event.longitude ? event.longitude : null
      this.calculateRouteDistance()
    },
    toggleRoundTrip (event) {
      this.mileage.roundTrip = event
      if (event) {
        if (this.mileage.distanceCustom) {
          this.mileage.distanceCustom = this.mileage.distanceCustom * 2
          this.distance = this.mileage.distanceCustom
        } else if (this.mileage.distance) {
          this.mileage.distance = this.mileage.distance * 2
          this.distance = this.mileage.distance
        }
        this.calculateAmount()
      } else {
        if (!event) {
          if (this.mileage.distanceCustom) {
            this.mileage.distanceCustom = this.mileage.distanceCustom / 2
            this.distance = this.mileage.distanceCustom
          } else if (this.mileage.distance) {
            this.mileage.distance = this.mileage.distance / 2
            this.distance = this.mileage.distance
          }
          this.calculateAmount()
        }
      }
    },
    calculateRouteDistance () {
      if (!this.mileage.origin.lat || !this.mileage.destination.lat) return

      this.googleRoutesError = false
      // eslint-disable-next-line
      const originCoordinates = new google.maps.LatLng(Number(this.mileage.origin.lat), Number(this.mileage.origin.lng))
      // eslint-disable-next-line
      const destinationCoordinates = new google.maps.LatLng(Number(this.mileage.destination.lat), Number(this.mileage.destination.lng))
      // eslint-disable-next-line
      const directionsService = new google.maps.DirectionsService()

      const request = {
        origin: originCoordinates,
        destination: destinationCoordinates,
        travelMode: 'DRIVING',
        // eslint-disable-next-line
        unitSystem: google.maps.UnitSystem.METRIC
      }
      directionsService.route(request, (response, status) => {
        switch (status) {
          case 'OK': {
            const distance = Number(response.routes[0].legs[0].distance.value / 1000)
            this.mileage.distance = this.mileage.roundTrip ? distance * 2 : distance
            this.mileage.distanceCustom = null
            this.distance = this.mileage.roundTrip ? distance * 2 : distance
            this.manualDistance = false
            this.calculateAmount()
            break
          }
          default: {
            this.googleRoutesError = true
            this.mileage.distance = null
            this.mileage.distanceCustom = null
            this.distance = null
            this.manualDistance = false
            this.calculateAmount()
          }
        }
      })
    },

    calculateAmount () {
      if (this.mileage.distanceCustom && this.mileage.category && this.mileage.category.rate) {
        this.mileage.amount = this.mileage.distanceCustom * this.mileage.category.rate
      } else if (this.mileage.distance && this.mileage.category && this.mileage.category.rate) {
        this.mileage.amount = this.mileage.distance * this.mileage.category.rate
      } else {
        this.mileage.amount = null
      }
    },
    async draftHandler () {
      if (this.isSubmitting) return
      if (this.transactionId) {
        await this.editMileage('DRAFT')
      } else {
        await this.createMileage('DRAFT')
      }
    },
    async submitMileageHandler () {
      if (this.transactionIsLocked) return
      if (this.fieldIsRequired('medias') && !this.transaction.withoutTicket && ((!this.transaction.receipt?.receiptMedia || this.transaction.receipt?.receiptMedia?.length === 0) && this.expensesReceipts.length === 0) && !this.withoutTicket.withoutTicket) {
        this.showSendToReviewWithoutReceipt = true
      } else {
        if (this.transactionId) {
          if (this.reviewStatus === REVIEW_STATUS.DRAFT) {
            await this.editMileage(REVIEW_STATUS.AWAITING_REVIEW)
            return
          } else {
            await this.editMileage()
            return
          }
        }
        await this.createMileage(REVIEW_STATUS.AWAITING_REVIEW)
      }
    },
    async createMileage (reviewStatus) {
      if (this.isSubmitting) return
      this.isSubmitting = true
      try {
        const { id } = this.expenseTypes.find(el => el.code === 'MILEAGE')
        const expense = {
          type: { id: id },
          amount: this.mileage.amount * -1,
          notes: this.notes,
          date: this.mileage.date,
          mileage: {
            origin: { ...this.mileage.origin },
            destination: { ...this.mileage.destination },
            roundTrip: this.mileage.roundTrip,
            distance: this.mileage.distance ? this.mileage.distance : null,
            distanceCustom: this.mileage.distanceCustom ? this.mileage.distanceCustom : null
          }

        }
        if (this.mileage.category && this.mileage.category.id) {
          expense.mileage.mileageCategory = { id: this.mileage.category.id }
        }
        if (this.budget) {
          expense.budget = { id: this.budget.id }
        }
        if (this.paymentMethod?.id) {
          expense.paymentMethod = { id: this.paymentMethod.id }
        }
        if (this.transactionTags.length > 0) {
          const formatedTransactionTags = this.transactionTags.map(el => {
            if (typeof el.tag.id === 'string') {
              const formated = {
                tag: {
                  name: el.tag.name,
                  type: { ...el.tag.type }
                }
              }
              return formated
            } else {
              return el
            }
          })
          expense.transaction = {}
          expense.transaction.transactionTags = formatedTransactionTags
        } else {
          expense.transaction = {}
          expense.transaction.transactionTags = []
        }

        if (this.withoutTicket.withoutTicket) {
          expense.transaction = { ...expense.transaction, ...this.withoutTicket }
        }

        expense.reviewStatusCode = reviewStatus
        await this.$store.dispatch('TransactionsStore/createTransaction', expense)
        const files = []
        const types = []
        this.expensesReceipts.forEach((el) => {
          files.push(el.file)
          types.push(el.media.type)
        })
        await this.$store.dispatch(
          'TransactionsStore/submitTransactionReceipt',
          { id: this.newExpense, files: files, types: types }
        )

        this.$emit('expenseCreated', { force: true })
        this.$store.dispatch('TransactionsStore/resetExpenseReceipts')
        this.closeModal()
      } catch (error) {
        console.error(error)
      } finally {
        this.isSubmitting = false
        if (this.$route.name !== 'my_history_reimbursements') {
          this.$router.push({ name: 'my_history_reimbursements' })
        }
      }
    },
    async editMileage (reviewStatus) {
      if (this.isSubmitting) return
      try {
        this.isSubmitting = true
        let editedMileage = {}
        const mileageInfo = {
          id: this.transaction.expense.mileage.id
        }

        if (this.expenseId) {
          editedMileage.id = this.expenseId
        }

        if (this.withoutTicket.withoutTicket) {
          editedMileage = { ...editedMileage, ...this.withoutTicket }
        }

        editedMileage.amount = this.mileage.amount >= 0 ? this.mileage.amount * -1 : null
        if (this.mileage.date) {
          editedMileage.date = this.mileage.date
        }
        if (this.notes) {
          editedMileage.notes = this.notes
        }

        if (this.mileage.origin?.lat) {
          mileageInfo.origin = { ...this.mileage.origin }
        }
        if (this.mileage.destination?.lat) {
          mileageInfo.destination = { ...this.mileage.destination }
        }
        if (typeof this.mileage.roundTrip === 'boolean') {
          mileageInfo.roundTrip = this.mileage.roundTrip
        }

        mileageInfo.distance = typeof this.mileage.distance === 'number' ? this.mileage.distance : null
        mileageInfo.distanceCustom = typeof this.mileage.distanceCustom === 'number' ? this.mileage.distanceCustom : null

        if (this.mileage?.category?.id) {
          mileageInfo.mileageCategory = { id: this.mileage.category.id }
        }
        if (this.paymentMethod?.id) {
          editedMileage.paymentMethod = { id: this.paymentMethod.id }
        }

        const transaction = {
          id: this.transactionId,
          expense: { ...editedMileage }
        }

        if (this.transactionTags.length > 0) {
          const formatedTransactionTags = this.transactionTags.map(el => {
            if (typeof el.tag.id === 'string') {
              const formated = {
                tag: {
                  name: el.tag.name,
                  type: { ...el.tag.type }
                }
              }
              return formated
            } else {
              return el
            }
          })

          transaction.transactionTags = formatedTransactionTags
        } else {
          transaction.transactionTags = []
        }

        if (this.budget) {
          transaction.budget = { id: this.budget.id }
        }
        transaction.expense.mileage = { ...mileageInfo }
        if (reviewStatus) {
          const { id } = this.reviewStatusTypes.find(el => el.code === reviewStatus)
          if (id) {
            transaction.reviewStatus = { id: id }
          }
        }

        await this.$store.dispatch('TransactionsStore/editTransaction', { transactionInformation: { ...transaction }, originalTransaction: this.transaction })
        this.closeModal()
      } catch (error) {
        console.error(error)
        this.$store.dispatch('SnackBars/addErrorHandler', { error: error, errorMessage: 'expense_edit_mileage_error' })
      } finally {
        this.isSubmitting = true
      }
    },

    _initialize () {
      this.originalExpense = this.transaction.expense
      this.mileage.amount = this.transaction.expense.amount ? Math.abs(this.transaction.expense.amount) : null
      this.mileage.date = this.transaction.expense.date ? this.transaction.expense.date.split('T')[0] : ''
      this.notes = this.transaction.expense.notes ? this.transaction.expense.notes : ''
      this.mileage.origin = this.transaction.expense.mileage && this.transaction.expense.mileage.origin ? { position: 'ORIGIN', ...this.transaction.expense.mileage.origin } : { position: 'ORIGIN' }
      this.origin = { ...this.mileage.origin }
      this.mileage.destination = this.transaction.expense.mileage && this.transaction.expense.mileage.destination ? { position: 'DESTINATION', ...this.transaction.expense.mileage.destination } : { position: 'DESTINATION' }
      this.destination = { ...this.mileage.destination }
      this.mileage.distance = this.transaction.expense.mileage && this.transaction.expense.mileage.distance ? this.transaction.expense.mileage.distance : null
      this.mileage.distanceCustom = this.transaction.expense.mileage && this.transaction.expense.mileage.distanceCustom ? this.transaction.expense.mileage.distanceCustom : null
      this.distance = this.transaction.expense.mileage?.distanceCustom || this.transaction.expense.mileage?.distance
      this.mileage.category = this.transaction.expense.mileage && this.transaction.expense.mileage.mileageCategory ? this.transaction.expense.mileage.mileageCategory : ''
      this.mileage.roundTrip = this.transaction.expense.mileage && this.transaction.expense.mileage.roundTrip ? this.transaction.expense.mileage.roundTrip : false
      this.budget = this.transaction && this.transaction.budget ? this.transaction.budget : null
      this.transactionReviewedFlag = this.transaction.reviewedAt !== null
      this.originalMileageData = { ...this.mileage }
      this.originalOriginData = { ...this.origin }
      this.originalDestinationData = { ...this.destination }
      this.originalBudgetId = this.budget
      this.paymentMethod = this.transaction.expense?.paymentMethod?.id ? { ...this.transaction.expense.paymentMethod } : this.paymentMethod
      this.originalPaymentMethod = { ...this.paymentMethod }
      this.transactionTags = this.transaction.transactionTags
        ? this.transaction.transactionTags
        : []
      this.originalTransactionTags = [...this.transactionTags]
    }
  },
  async created () {
    this.$store.dispatch('TransactionsStore/clearCurrentTransaction')
    this.isInitializing = true
    const promises = []
    this.mileage = generateMileageBaseModel()
    this.originalMileageData = { ...this.mileage }
    this.originalOriginData = { ...this.origin }
    this.originalDestinationData = { ...this.destination }

    promises.push(this.$store.dispatch('TransactionsStore/resetCurrentTransaction'))
    if (this.transactionId) {
      await this.fetchTransaction()
      this.mapsId++
    }
    promises.push(this.fetchPaymentMethods())
    await this.fetchCurrentTransactionUserBudgets()
    if (this.isSuperBook) {
      const filters = {
        'transaction.id': this.transactionId
      }

      promises.push(this.$store.dispatch('CompanyStore/ExpensesSettingsStore/setMileageCategoriesFilters', filters))
    } else {
      promises.push(this.$store.dispatch('CompanyStore/ExpensesSettingsStore/fetchMileageCategories', { pagination: false }))
    }

    Promise.allSettled(promises).then(() => {
      if (this.paymentMethods.length === 1) {
        this.paymentMethod = { ...this.paymentMethods[0] }
      }

      if (this.transactionId) {
        this._initialize()
        this.checkPaymentMethodInList()
      } else {
        if (this.budgets.length === 1) {
          this.budget = this.budgets[0]?.budget
        }
      }
      this.getBudgetTagTypes(INCLUDE_IN_TRANSACTION_TYPES.MILEAGE)
      this.isInitializing = false
    })
  },
  watch: {
    transactionId: async function (newV) {
      if (newV) {
        this.isInitializing = true
        await this.fetchTransaction()
        await this.fetchPaymentMethods()
        this._initialize()
        await this.getBudgetTagTypes(INCLUDE_IN_TRANSACTION_TYPES.MILEAGE)
        this.mapsId++
        this.isInitializing = false
      }
    }

  },

  destroyed () {
    this.$store.dispatch('TransactionsStore/clearCurrentTransaction')
    this.$store.dispatch('TransactionsStore/resetExpenseReceipts')
  }
}

</script>
<style scoped lang="scss" src="@/assets/styles/transactions/transactions-edit.scss"></style>

<style scoped lang="scss">
.multiselect-filter >>> .multiselect .multiselect__tags .multiselect--error {
  border: 2px solid red;
  border-radius: 6px;
}
.checkbox-container >>> div {
  justify-content: flex-start !important;
}

@media (min-width: 576px) {
  .ModalBox {
    max-width: 792px;
    width: 100%;
  }
}

.manual-distance {
  font-weight: 400;
  font-size: 13px;
  line-height: 24px;
  color: var(--text-heading);
  margin-left: 6px;
  margin-top: -10px;
}
</style>
<style lang="scss">
.grid-mileage {
  display: grid;
  grid-template-columns: 364px 360px;
  grid-gap: 24px;

  &--first-column {
    grid-column: 2 / 3;
  }

  &--map-column {
    grid-column: 1 / 2;
    grid-row: 1/-1;
  }
}
.map--smaller {
  display: none;
}

@media screen and (max-width: 576px) {
  .grid-mileage {
    grid-template-columns: 100%;
    &--first-column {
      grid-column: 1/2;
    }
    &--footer {
      padding: 0px 20px;
      grid-gap: 0px;
    }
    &--map-column {
      display: none;
    }
  }
  .map--smaller {
    display: block;
    max-height: 300px;
    height: 100%;
  }
}

.map-content {
  width: 370px !important;
  position: fixed !important;
  height: calc(95vh - 72px) !important;
}
.Fuell-Button {
  width: 348px;
}
#funnel-actions > .Fuell-Button:nth-child(2) {
  margin-right: 0px;
}
</style>
